import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useCallStore } from '@/store/callStore';
import type { Message, DirectMessage } from '@/types';
import type { Notification } from '@/api/notifications';
import type { Call } from '@/api/calls';
import { syncApi, seqGt, type SyncEvent } from '@/api/sync';
import { apiClient } from '@/api/client';

export function useSocketEvents() {
  const { isAuthenticated } = useAuthStore();
  const {
    addChannelMessage,
    updateChannelMessage,
    removeChannelMessage,
    addDMMessage,
    setTyping,
    setDMTyping,
    setUserStatus,
    activeChannelId,
    incrementUnread,
  } = useChatStore();
  const { addNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) return;

    const socket = getSocket();

    // ── Phase 7 — gap detection / dolling sync ──────────────────────────
    // Применяем массив SyncEvent'ов к локальному стору. Это «бедный»
    // ререндер: для большинства типов событий мы просто инвалидируем
    // tanstack-query'ы, чтобы UI догрузил каноническую версию из БД.
    // Это проще, чем заводить отдельные mappers для каждого payload'а,
    // и достаточно для целей gap recovery.
    const applyChannelEvents = (channelId: string, events: SyncEvent[]) => {
      if (!events.length) return;
      queryClient.invalidateQueries({ queryKey: ['messages', 'channel', channelId] });
      queryClient.invalidateQueries({ queryKey: ['pinned', channelId] });
      const last = events[events.length - 1].seq;
      useChatStore.getState().bumpChannelSeq(channelId, last);
    };
    const applyDmEvents = (peerId: string, events: SyncEvent[]) => {
      if (!events.length) return;
      queryClient.invalidateQueries({ queryKey: ['messages', 'dm', peerId] });
      const last = events[events.length - 1].seq;
      useChatStore.getState().bumpDmSeq(peerId, last);
    };

    const runChannelSync = async (channelId: string, afterSeq: string) => {
      try {
        let cursor = afterSeq;
        // Защитимся от бесконечного цикла на случай битого ответа.
        for (let i = 0; i < 10; i++) {
          const page = await syncApi.syncChannel(channelId, cursor);
          applyChannelEvents(channelId, page.events);
          if (!page.hasMore) break;
          cursor = page.nextSeq;
        }
      } catch { /* мягкий fail — реалтайм всё равно подтянет */ }
    };
    const runDmSync = async (peerId: string, afterSeq: string) => {
      try {
        let cursor = afterSeq;
        for (let i = 0; i < 10; i++) {
          const page = await syncApi.syncDm(peerId, cursor);
          applyDmEvents(peerId, page.events);
          if (!page.hasMore) break;
          cursor = page.nextSeq;
        }
      } catch { /* noop */ }
    };

    const maybeFillChannelGap = (channelId: string, seq?: string) => {
      if (!seq) return;
      const local = useChatStore.getState().channelSeq[channelId];
      if (!local) return; // первая загрузка — без локального cursor'а гэпов не считаем
      try {
        if (BigInt(seq) > BigInt(local) + 1n) {
          void runChannelSync(channelId, local);
        }
      } catch { /* noop */ }
    };
    const maybeFillDmGap = (partnerId: string, seq?: string) => {
      if (!seq) return;
      const local = useChatStore.getState().dmSeq[partnerId];
      if (!local) return;
      try {
        if (BigInt(seq) > BigInt(local) + 1n) {
          void runDmSync(partnerId, local);
        }
      } catch { /* noop */ }
    };

    // v5.7.15 — backfill missed messages after socket reconnect.
    // Сценарий: tab был в background / network дропнулся → socket
    // disconnected → bot.sendMessage emit'ы не доставились → reconnect →
    // только новые events приходят, missed остаются gap'ом в state.
    //
    // На каждый connect ПОСЛЕ первого (это reconnect) → для activeChannelId
    // делаем full refetch первой страницы + runChannelSync from local seq.
    // refetch покрывает случай без локального seq, runChannelSync — gap.
    const isFirstConnectRef = { current: true };
    const handleConnect = () => {
      if (isFirstConnectRef.current) {
        isFirstConnectRef.current = false;
        return;
      }
      // Reconnect — backfill active channel.
      const activeId = useChatStore.getState().activeChannelId;
      if (activeId) {
        void queryClient.refetchQueries({
          queryKey: ['messages', 'channel', activeId],
          type: 'active',
        });
        const localSeq = useChatStore.getState().channelSeq[activeId];
        if (localSeq) {
          void runChannelSync(activeId, localSeq);
        }
      }
    };
    socket.on('connect', handleConnect);

    socket.on('message:new', (msg) => {
      const message = msg as Message;
      maybeFillChannelGap(message.channelId, message.seq);
      addChannelMessage(message);
      if (message.channelId !== activeChannelId) {
        incrementUnread(message.channelId);
      }
    });

    socket.on('message:edited', (msg) => {
      updateChannelMessage(msg as Message);
    });

    socket.on('message:deleted', ({ id, channelId }) => {
      removeChannelMessage(id, channelId);
    });

    socket.on('dm:new', (msg) => {
      const dm = msg as DirectMessage;
      const me = useAuthStore.getState().user?.id;
      const peer = me === dm.senderId ? dm.recipientId : dm.senderId;
      maybeFillDmGap(peer, dm.seq);
      addDMMessage(dm);
    });

    // Phase 16 — dm:deleted
    // - { dmId, peerId, localOnly?: boolean }
    // - localOnly: инициатор сам запросил «у меня» — синк его же других
    //   вкладок (делаем removeDmMessage).
    // - без localOnly (scope='all'): собеседник или я удалил у всех —
    //   помечаем message как deleted-for-all (ставим плашку «удалено»).
    socket.on('dm:deleted', (payload: { dmId: string; peerId: string; localOnly?: boolean }) => {
      const { removeDmMessage, markDmDeleted } = useChatStore.getState();
      if (payload.localOnly) {
        removeDmMessage(payload.dmId);
      } else {
        markDmDeleted(payload.dmId);
      }
    });

    // Phase 16 — dm:cleared. Один собеседник очистил переписку.
    // scope='me'       — синк моих других вкладок; очистить локально
    //                    dmMessages[peerId].
    // scope='all'      — я сам очистил у всех: dmMessages[peerId] → [].
    // scope='peer-all' — собеседник очистил у всех на моей стороне: мои
    //                    сообщения останутся, его сообщения стали «удалены».
    //                    Перезагружаем список через invalidate (проще всего).
    socket.on('dm:cleared', (payload: { peerId: string; scope: 'me' | 'all' | 'peer-all' }) => {
      const { setDMMessages } = useChatStore.getState();
      if (payload.scope === 'me' || payload.scope === 'all') {
        setDMMessages(payload.peerId, []);
      } else if (payload.scope === 'peer-all') {
        // На нашей стороне собеседник не удалял наши сообщения; свои — удалил.
        // Простой путь: invalidate — React Query перезагрузит с сервера
        // уже с актуальным content='[message deleted]' для его сообщений.
        // Так как мы не знаем queryClient тут — явно перезагружаем dmMessages.
        setDMMessages(payload.peerId, []);
      }
    });

    socket.on('typing:update', ({ channelId, users }) => {
      setTyping(channelId, users);
    });

    socket.on('dm:typing', ({ fromUserId, isTyping }) => {
      setDMTyping(fromUserId, isTyping);
    });

    socket.on('user:status', ({ userId, status }) => {
      setUserStatus(userId, status);
    });

    socket.on('notification:new', (notif) => {
      addNotification(notif as Notification);
    });

    // Reactions / pinning — these are not in the typed event map yet. We
    // bridge them as DOM CustomEvents so any component can subscribe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sock = socket as any;
    sock.on('reaction:update', (payload: { messageId: string; channelId: string; summary: unknown }) => {
      window.dispatchEvent(new CustomEvent('reaction:update', { detail: payload }));
    });
    sock.on('dm:reaction:update', (payload: { dmId: string; summary: unknown }) => {
      window.dispatchEvent(new CustomEvent('dm:reaction:update', { detail: payload }));
    });
    sock.on('message:pinned', (payload: { messageId: string; channelId: string; pinnedAt: string | Date | null }) => {
      window.dispatchEvent(new CustomEvent('message:pinned', { detail: payload }));
    });
    sock.on('message:unpinned', (payload: { messageId: string; channelId: string }) => {
      window.dispatchEvent(new CustomEvent('message:unpinned', { detail: payload }));
    });

    // ── Call signaling ───────────────────────────────────────────────────────
    // The call store holds its own reducer-style methods; we just forward
    // raw socket events into them. This keeps the hook dumb and lets the
    // store be unit-tested in isolation without a live socket.
    const callStore = useCallStore.getState;
    sock.on('call:incoming', ({ call }: { call: Call }) => {
      callStore().handleIncoming(call);
    });
    sock.on('call:state', ({ call }: { call: Call }) => {
      void callStore().onCallState(call);
      // History UI may be open — invalidate.
      queryClient.invalidateQueries({ queryKey: ['calls', 'history'] });
    });
    sock.on('call:offer', ({ callId, sdp }: { callId: string; sdp: string }) => {
      void callStore().onOffer(callId, sdp);
    });
    sock.on('call:answer', ({ callId, sdp }: { callId: string; sdp: string }) => {
      void callStore().onAnswer(callId, sdp);
    });
    sock.on('call:ice-candidate', ({ callId, candidate }: { callId: string; candidate: unknown }) => {
      void callStore().onIceCandidate(callId, candidate);
    });
    sock.on('call:error', ({ message }: { message: string }) => {
      callStore().onError(message);
    });

    // After reconnect — caches may be stale; refetch active queries.
    // Phase 7 — дополнительно делаем server-state sync: получаем
    // курсоры по всем чатам и для тех, где локальный seq отстал,
    // запрашиваем дельту через `/api/sync/{channel|dm}/...`. Это
    // компенсирует пропущенные realtime-события на время отвала.
    // Check for active call on (re)connect — handles PWA reopen from push notification
    const checkActiveCall = async () => {
      try {
        const { data } = await apiClient.get<{ call: Call | null }>('/api/calls/active');
        if (data.call && (data.call.status === 'RINGING' || data.call.status === 'ACCEPTED')) {
          const cs = useCallStore.getState();
          if (cs.phase === 'idle') {
            cs.handleIncoming(data.call);
          }
        }
      } catch { /* non-critical */ }
    };
    void checkActiveCall();

    const onReconnect = () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['channel'] });
      queryClient.invalidateQueries({ queryKey: ['pinned'] });
      queryClient.invalidateQueries({ queryKey: ['dm-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['calls', 'history'] });
      void checkActiveCall();

      void (async () => {
        try {
          const cursors = await syncApi.getCursors();
          const state = useChatStore.getState();
          for (const c of cursors.channels) {
            const local = state.channelSeq[c.channelId];
            if (local && seqGt(c.lastSeq, local)) {
              await runChannelSync(c.channelId, local);
            } else if (!local) {
              // Локального cursor'а нет вовсе — запомним серверный, чтобы
              // следующее входящее сообщение могло корректно сравниться.
              state.setChannelSeq(c.channelId, c.lastSeq);
            }
          }
          for (const d of cursors.dms) {
            const local = state.dmSeq[d.peerId];
            if (local && seqGt(d.lastSeq, local)) {
              await runDmSync(d.peerId, local);
            } else if (!local) {
              state.setDmSeq(d.peerId, d.lastSeq);
            }
          }
        } catch { /* мягкий fail */ }
      })();
    };
    sock.io?.on?.('reconnect', onReconnect);

    // Phase 7 + 15.2 — на холодном маунте подтягиваем курсоры И догоняем
    // дельту. До Phase 15.1 chatStore был пуст после reload, поэтому cold
    // mount только SET'ил курсоры. Теперь chatStore гидрируется из IDB и
    // может содержать устаревшие курсоры — значит надо активно тянуть
    // события, не ждать реконнекта сокета.
    void (async () => {
      try {
        const cursors = await syncApi.getCursors();
        const state = useChatStore.getState();
        for (const c of cursors.channels) {
          const local = state.channelSeq[c.channelId];
          if (local && seqGt(c.lastSeq, local)) {
            await runChannelSync(c.channelId, local);
          } else if (!local) {
            state.setChannelSeq(c.channelId, c.lastSeq);
          }
        }
        for (const d of cursors.dms) {
          const local = state.dmSeq[d.peerId];
          if (local && seqGt(d.lastSeq, local)) {
            await runDmSync(d.peerId, local);
          } else if (!local) {
            state.setDmSeq(d.peerId, d.lastSeq);
          }
        }
      } catch { /* noop */ }
    })();

    return () => {
      socket.off('connect', handleConnect);
      socket.off('message:new');
      socket.off('message:edited');
      socket.off('message:deleted');
      socket.off('dm:new');
      socket.off('typing:update');
      socket.off('dm:typing');
      socket.off('user:status');
      socket.off('notification:new');
      sock.off('reaction:update');
      sock.off('dm:reaction:update');
      sock.off('message:pinned');
      sock.off('message:unpinned');
      sock.off('call:incoming');
      sock.off('call:state');
      sock.off('call:offer');
      sock.off('call:answer');
      sock.off('call:ice-candidate');
      sock.off('call:error');
      sock.io?.off?.('reconnect', onReconnect);
    };
  }, [
    isAuthenticated,
    activeChannelId,
    addChannelMessage,
    updateChannelMessage,
    removeChannelMessage,
    addDMMessage,
    setTyping,
    setDMTyping,
    setUserStatus,
    incrementUnread,
    addNotification,
    queryClient,
  ]);
}

export function useTypingEmitter(channelId: string | null) {
  const socket = getSocket();
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTyping = useCallback(() => {
    if (!channelId) return;
    socket.emit('typing:start', { channelId });
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      socket.emit('typing:stop', { channelId });
    }, 3000);
  }, [channelId, socket]);

  const stopTyping = useCallback(() => {
    if (!channelId) return;
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = null;
    socket.emit('typing:stop', { channelId });
  }, [channelId, socket]);

  useEffect(() => {
    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
    };
  }, []);

  return { startTyping, stopTyping };
}
